// The port's stand-in for structuredClone, which jsdom 16 (jest 27 under react-scripts 5) does not
// have. companion/src calls structuredClone on plain JSON documents only: verify.mjs:180 and :187
// (an appended step, a fixture), plan.mjs:245 and :255 (a step), compile.mjs:202 and :408 (markers,
// a fixture). Plain data in, an equal and independent plain-data copy out: nothing is shared with
// the source afterwards, keys holding undefined stay (as structuredClone keeps them), cycles are
// honoured, and a function or a symbol is refused the way structuredClone refuses it.
// basil-verify keeps its own copy of this: the verifier never imports basil-core.

type Seen = Map<object, unknown>;

function cloneFrom(value: unknown, seen: Seen): unknown {
  if (typeof value === 'function' || typeof value === 'symbol') {
    throw new TypeError(`deepClone cannot copy a ${typeof value}`);
  }
  if (value === null || typeof value !== 'object') return value;
  const known = seen.get(value);
  if (known !== undefined) return known;
  if (Array.isArray(value)) {
    const copy: unknown[] = [];
    seen.set(value, copy);
    value.forEach((item, index) => {
      copy[index] = cloneFrom(item, seen);
    });
    return copy;
  }
  if (value instanceof Date) return new Date(value.getTime());
  if (value instanceof Map) {
    const copy = new Map<unknown, unknown>();
    seen.set(value, copy);
    value.forEach((item, key) => {
      copy.set(cloneFrom(key, seen), cloneFrom(item, seen));
    });
    return copy;
  }
  if (value instanceof Set) {
    const copy = new Set<unknown>();
    seen.set(value, copy);
    value.forEach((item) => {
      copy.add(cloneFrom(item, seen));
    });
    return copy;
  }
  const source = value as Record<string, unknown>;
  const copy: Record<string, unknown> = {};
  seen.set(value, copy);
  Object.keys(source).forEach((key) => {
    copy[key] = cloneFrom(source[key], seen);
  });
  return copy;
}

export function deepClone<T>(value: T): T {
  return cloneFrom(value, new Map()) as T;
}
